/**
 * Single place that knows how to talk to the nu--comments-chats backend.
 *
 * Right now все методы возвращают моки (mockApi.ts) — фронт работает автономно
 * для UX-ревью. Замена на реальный fetch — точечная: каждый метод делает один
 * вызов того, что уже есть в Nest-контроллерах:
 *
 *   GET    /campaigns                   →  list campaigns
 *   GET    /campaigns/:id               →  campaign detail (+targets, +dialogs)
 *   PATCH  /campaigns/:id               →  update prompts/limits/schedule
 *   POST   /campaigns/:id/status/:s     →  start/pause/complete
 *   POST   /campaigns/:id/accounts      →  attach accounts
 *   POST   /campaigns/:id/targets       →  bulk add chats          (TODO ctrl)
 *   POST   /campaigns/:id/health        →  evaluate all chats      (TODO ctrl)
 *   POST   /dialog-templates/campaign/:id/generate
 *   POST   /dialog-templates/:id/{approve|reject|regenerate}
 *   POST   /dialog-templates/step/:stepId/edit
 *   GET    /accounts                    →  global account list
 *   POST   /accounts/bulk               →  bulk import sessionStrings
 *   POST   /accounts/login/start|finish →  phone-login wizard
 *   POST   /reports/campaign/:id/generate
 *   GET    /reports/campaign/:id        →  list of reports
 *   GET    /reports/:id/download        →  xlsx file
 */

import * as mock from './mockApi';
import type {
  Account,
  AccountCheck,
  AccountDialog,
  CampaignAccountAssignment,
  CampaignTeamMember,
  PaginatedAccountLogs,
  Campaign,
  CampaignHistoryResponse,
  CampaignLogsResponse,
  CampaignLaunchExecuteResult,
  CampaignLaunchPreview,
  CampaignStatus,
  LaunchMode,
  ChatMessage,
  DialogTemplate,
  Incoming,
  InboxThread,
  Report,
  Target,
  TargetBulkDeleteResult,
  TargetDeletePreview,
  TargetDetail,
  ManualDialogStepInput,
} from '@/types/domain';

const BASE = import.meta.env.VITE_API_BASE || ''; // empty → use mock
const USE_MOCK = !BASE;

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      // Real TMA: forward initData so backend can validate user.
      'X-Telegram-Init-Data': window.Telegram?.WebApp?.initData ?? '',
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json() as Promise<T>;
}

export const api = {
  // ---------------------- campaigns ----------------------
  listCampaigns: (): Promise<Campaign[]> =>
    USE_MOCK ? mock.listCampaigns() : http('/campaigns'),

  getCampaign: (id: number): Promise<Campaign> =>
    USE_MOCK ? mock.getCampaign(id) : http(`/campaigns/${id}`),

  updateCampaign: (id: number, patch: Partial<Campaign>): Promise<Campaign> =>
    USE_MOCK
      ? mock.updateCampaign(id, patch)
      : http(`/campaigns/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  setCampaignStatus: (id: number, status: CampaignStatus): Promise<Campaign> =>
    USE_MOCK
      ? mock.setCampaignStatus(id, status)
      : http(`/campaigns/${id}/status/${status}`, { method: 'POST' }),

  previewCampaignLaunch: (id: number, mode: LaunchMode): Promise<CampaignLaunchPreview> =>
    USE_MOCK
      ? mock.previewCampaignLaunch(id, mode)
      : http(`/campaigns/${id}/launch/preview`, {
          method: 'POST',
          body: JSON.stringify({ mode }),
        }),

  executeCampaignLaunch: (id: number, mode: LaunchMode): Promise<CampaignLaunchExecuteResult> =>
    USE_MOCK
      ? mock.executeCampaignLaunch(id, mode)
      : http(`/campaigns/${id}/launch/execute`, {
          method: 'POST',
          body: JSON.stringify({ mode }),
        }),

  deleteCampaign: (id: number): Promise<{ ok: boolean }> =>
    USE_MOCK
      ? mock.deleteCampaign(id)
      : http(`/campaigns/${id}`, { method: 'DELETE' }),

  // ---------------------- targets / chats ----------------
  listTargets: (campaignId: number): Promise<Target[]> =>
    USE_MOCK ? mock.listTargets(campaignId) : http(`/campaigns/${campaignId}/targets`),

  addTargets: (campaignId: number, raw: string[]): Promise<{ added: number; duplicates: number; invalid: number }> =>
    USE_MOCK
      ? mock.addTargets(campaignId, raw)
      : http(`/campaigns/${campaignId}/targets`, { method: 'POST', body: JSON.stringify({ raw }) }),

  evaluateAllTargets: (
    campaignId: number,
    payload?: { audienceDescription?: string; taSignals?: string[] },
  ): Promise<{ ok: boolean; audienceDescription?: string | null; taSignals?: string[] }> =>
    USE_MOCK
      ? mock.evaluateAllTargets(campaignId, payload)
      : http(`/campaigns/${campaignId}/health`, {
          method: 'POST',
          body: JSON.stringify(payload ?? {}),
        }),

  removeTarget: (campaignId: number, targetId: number): Promise<{ ok: boolean }> =>
    USE_MOCK
      ? mock.removeTarget(campaignId, targetId)
      : http(`/campaigns/${campaignId}/targets/${targetId}`, { method: 'DELETE' }),

  previewTargetDelete: (campaignId: number, health: Target['health'][]): Promise<TargetDeletePreview> =>
    USE_MOCK
      ? mock.previewTargetDelete(campaignId, health)
      : http(`/campaigns/${campaignId}/targets/delete-preview`, {
          method: 'POST',
          body: JSON.stringify({ health }),
        }),

  bulkDeleteTargets: (campaignId: number, targetIds: number[]): Promise<TargetBulkDeleteResult> =>
    USE_MOCK
      ? mock.bulkDeleteTargets(campaignId, targetIds)
      : http(`/campaigns/${campaignId}/targets/delete`, {
          method: 'POST',
          body: JSON.stringify({ targetIds }),
        }),

  getTargetDetail: (campaignId: number, targetId: number): Promise<TargetDetail> =>
    USE_MOCK
      ? mock.getTargetDetail(campaignId, targetId)
      : http(`/campaigns/${campaignId}/targets/${targetId}`),

  distributeCampaign: (campaignId: number): Promise<{
    ok: boolean;
    status: string;
    accounts: number;
    targets: number;
    postingsCreated: number;
    joins: { scheduled: number; targets: number; accounts: number };
  }> =>
    USE_MOCK
      ? mock.distributeCampaign(campaignId)
      : http(`/campaigns/${campaignId}/distribute`, { method: 'POST' }),

  // ---------------------- accounts -----------------------
  listAccounts: (): Promise<Account[]> =>
    USE_MOCK ? mock.listAccounts() : http('/accounts'),

  getAccount: (accountId: number): Promise<Account> =>
    USE_MOCK ? mock.getAccount(accountId) : http(`/accounts/${accountId}`),

  checkAccount: (accountId: number): Promise<AccountCheck> =>
    USE_MOCK ? mock.checkAccount(accountId) : http(`/accounts/${accountId}/check`, { method: 'POST' }),

  listAccountDialogs: (accountId: number): Promise<AccountDialog[]> =>
    USE_MOCK ? mock.listAccountDialogs(accountId) : http(`/accounts/${accountId}/dialogs`),

  listAccountLogs: (
    accountId: number,
    scope: 'upcoming' | 'completed',
    page = 1,
    pageSize = 20,
  ): Promise<PaginatedAccountLogs> =>
    USE_MOCK
      ? mock.listAccountLogs(accountId, scope, page, pageSize)
      : http(`/accounts/${accountId}/logs?scope=${scope}&page=${page}&pageSize=${pageSize}`),

  attachAccounts: (
    campaignId: number,
    accounts: CampaignAccountAssignment[],
  ): Promise<{ added: number; updated?: number }> =>
    USE_MOCK
      ? mock.attachAccounts(campaignId, accounts)
      : http(`/campaigns/${campaignId}/accounts`, {
          method: 'POST',
          body: JSON.stringify({ accounts }),
        }),

  listAttachedAccounts: (campaignId: number): Promise<Account[]> =>
    USE_MOCK
      ? mock.listAttachedAccounts(campaignId)
      : http(`/campaigns/${campaignId}/accounts`),

  detachAccount: (campaignId: number, accountId: number): Promise<{ ok: boolean }> =>
    USE_MOCK
      ? mock.detachAccount(campaignId, accountId)
      : http(`/campaigns/${campaignId}/accounts/${accountId}`, { method: 'DELETE' }),

  listCampaignTeam: (campaignId: number): Promise<CampaignTeamMember[]> =>
    USE_MOCK ? mock.listCampaignTeam(campaignId) : http(`/campaigns/${campaignId}/team`),

  addCampaignTeamMember: (
    campaignId: number,
    payload: { tgId?: string; username?: string; role?: 'owner' | 'member' },
  ): Promise<{ ok: boolean; member: CampaignTeamMember }> =>
    USE_MOCK
      ? mock.addCampaignTeamMember(campaignId, payload)
      : http(`/campaigns/${campaignId}/team`, {
          method: 'POST',
          body: JSON.stringify(payload),
        }),

  removeCampaignTeamMember: (campaignId: number, userId: number): Promise<{ ok: boolean }> =>
    USE_MOCK
      ? mock.removeCampaignTeamMember(campaignId, userId)
      : http(`/campaigns/${campaignId}/team/${userId}`, { method: 'DELETE' }),

  // ---------------------- dialogs ------------------------
  listDialogs: (campaignId: number): Promise<DialogTemplate[]> =>
    USE_MOCK ? mock.listDialogs(campaignId) : http(`/dialog-templates/campaign/${campaignId}`),

  generateDialogs: (campaignId: number, count = 5): Promise<DialogTemplate[]> =>
    USE_MOCK
      ? mock.generateDialogs(campaignId, count)
      : http(`/dialog-templates/campaign/${campaignId}/generate`, {
          method: 'POST',
          body: JSON.stringify({ count }),
        }),

  createManualDialog: (campaignId: number, steps: ManualDialogStepInput[]): Promise<DialogTemplate> =>
    USE_MOCK
      ? mock.createManualDialog(campaignId, steps)
      : http(`/dialog-templates/campaign/${campaignId}/manual`, {
          method: 'POST',
          body: JSON.stringify({ steps }),
        }),

  approveDialog: (id: number) =>
    USE_MOCK ? mock.approveDialog(id) : http(`/dialog-templates/${id}/approve`, { method: 'POST' }),

  rejectDialog: (id: number) =>
    USE_MOCK ? mock.rejectDialog(id) : http(`/dialog-templates/${id}/reject`, { method: 'POST' }),

  regenerateDialog: (id: number, hint?: string) =>
    USE_MOCK
      ? mock.regenerateDialog(id, hint)
      : http(`/dialog-templates/${id}/regenerate`, { method: 'POST', body: JSON.stringify({ hint }) }),

  // ---------------------- inbox --------------------------
  listIncoming: (): Promise<Incoming[]> =>
    USE_MOCK ? mock.listIncoming() : http('/inbox'),

  // ---------------------- reports ------------------------
  listReports: (campaignId: number): Promise<Report[]> =>
    USE_MOCK ? mock.listReports(campaignId) : http(`/reports/campaign/${campaignId}`),

  generateReport: (campaignId: number): Promise<Report> =>
    USE_MOCK
      ? mock.generateReport(campaignId)
      : http(`/reports/campaign/${campaignId}/generate`, { method: 'POST' }),

  // ---------------------- inbox threads (campaign-scoped) ----------------------
  listInboxThreads: (campaignId: number): Promise<InboxThread[]> =>
    USE_MOCK ? mock.listInboxThreads(campaignId) : http(`/campaigns/${campaignId}/inbox`),

  getCampaignHistory: (campaignId: number): Promise<CampaignHistoryResponse> =>
    USE_MOCK ? mock.getCampaignHistory(campaignId) : http(`/campaigns/${campaignId}/history`),

  listCampaignLogs: (
    campaignId: number,
    scope: 'upcoming' | 'completed',
    type: 'all' | 'join' | 'seed',
  ): Promise<CampaignLogsResponse> =>
    USE_MOCK
      ? mock.listCampaignLogs(campaignId, scope, type)
      : http(`/campaigns/${campaignId}/logs?scope=${scope}&type=${type}`),

  getInboxThread: (threadId: string): Promise<InboxThread> =>
    USE_MOCK ? mock.getInboxThread(threadId) : http(`/inbox/${threadId}`),

  sendInboxMessage: (threadId: string, text: string): Promise<ChatMessage> =>
    USE_MOCK
      ? mock.sendInboxMessage(threadId, text)
      : http(`/inbox/${threadId}/messages`, { method: 'POST', body: JSON.stringify({ text }) }),

  // ---------------------- create campaign --------------------------------------
  createCampaign: (data: Partial<Campaign>): Promise<Campaign> =>
    USE_MOCK ? mock.createCampaign(data) : http(`/campaigns`, { method: 'POST', body: JSON.stringify(data) }),
};
